import type { OwnerKind } from "@coach/shared";
import type { LocalDb } from "@coach/sync";
import { enqueueOp, newId, nowIso, selectAll, withLocalWrite } from "./base.js";

/** Team row (mirrors `team` table — single tenant per row). */
export interface TeamRow {
  id: string;
  org_id: string;
  rev: number | null;
  updated_at: string;
  deleted_at: string | null;
  client_id: string | null;
  name: string;
  league: string | null;
}

/** Team-coach assignment row (mirrors `team_coach` join table). */
export interface TeamCoachRow {
  id: string;
  org_id: string;
  rev: number | null;
  updated_at: string;
  deleted_at: string | null;
  client_id: string | null;
  team_id: string;
  user_id: string;
  role: string;
}

export async function listTeams(
  db: LocalDb,
  orgId: string,
): Promise<TeamRow[]> {
  return selectAll<TeamRow>(
    db,
    `SELECT * FROM team WHERE org_id = ? AND deleted_at IS NULL ORDER BY name ASC`,
    [orgId],
  );
}

/** Teams the given user has a `team_coach` link to. Use for non-owner views. */
export async function listTeamsForUser(
  db: LocalDb,
  orgId: string,
  userId: string,
): Promise<TeamRow[]> {
  return selectAll<TeamRow>(
    db,
    `SELECT t.* FROM team t
       INNER JOIN team_coach tc ON tc.team_id = t.id AND tc.deleted_at IS NULL
       WHERE t.org_id = ? AND t.deleted_at IS NULL AND tc.user_id = ?
       ORDER BY t.name ASC`,
    [orgId, userId],
  );
}

export async function listTeamCoaches(
  db: LocalDb,
  orgId: string,
  teamId: string,
): Promise<TeamCoachRow[]> {
  return selectAll<TeamCoachRow>(
    db,
    `SELECT * FROM team_coach
       WHERE org_id = ? AND team_id = ? AND deleted_at IS NULL
       ORDER BY role ASC, user_id ASC`,
    [orgId, teamId],
  );
}

export interface CreateTeamInput {
  orgId: string;
  name: string;
  league?: string | null;
}

export async function createTeam(
  db: LocalDb,
  input: CreateTeamInput,
): Promise<TeamRow> {
  const id = newId();
  const updatedAt = nowIso();
  const league = input.league ?? null;
  const row: TeamRow = {
    id,
    org_id: input.orgId,
    rev: null,
    updated_at: updatedAt,
    deleted_at: null,
    client_id: null,
    name: input.name,
    league,
  };

  await withLocalWrite(db, async (tx) => {
    await tx.exec(
      `INSERT INTO team (id, org_id, rev, updated_at, deleted_at, client_id, name, league)
         VALUES (?, ?, NULL, ?, NULL, NULL, ?, ?)`,
      [id, input.orgId, updatedAt, input.name, league],
    );
    await enqueueOp(tx, {
      entity: "team",
      kind: "upsert",
      payload: {
        id,
        orgId: input.orgId,
        name: input.name,
        league,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: null,
    });
  });

  return row;
}

export interface UpdateTeamInput {
  id: string;
  orgId: string;
  name?: string;
  league?: string | null;
}

/** Local update for a team (rename / set league). Queued for sync. */
export async function updateTeam(
  db: LocalDb,
  input: UpdateTeamInput,
): Promise<TeamRow | null> {
  const existing = (
    await selectAll<TeamRow>(db, `SELECT * FROM team WHERE id = ? LIMIT 1`, [
      input.id,
    ])
  )[0];
  if (!existing || existing.deleted_at) return null;

  const updatedAt = nowIso();
  const nextName = input.name ?? existing.name;
  const nextLeague =
    input.league === undefined
      ? existing.league
      : input.league === null || input.league === ""
        ? null
        : input.league;

  const row: TeamRow = {
    ...existing,
    name: nextName,
    league: nextLeague,
    updated_at: updatedAt,
  };

  await withLocalWrite(db, async (tx) => {
    await tx.exec(
      `UPDATE team SET name = ?, league = ?, updated_at = ? WHERE id = ?`,
      [nextName, nextLeague, updatedAt, input.id],
    );
    await enqueueOp(tx, {
      entity: "team",
      kind: "upsert",
      payload: {
        id: input.id,
        orgId: input.orgId,
        name: nextName,
        league: nextLeague,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: existing.rev ?? null,
    });
  });

  return row;
}

export interface AssignCoachInput {
  orgId: string;
  teamId: string;
  userId: string;
  role?: "coach" | "assistant";
}

export async function assignCoachToTeam(
  db: LocalDb,
  input: AssignCoachInput,
): Promise<TeamCoachRow> {
  const updatedAt = nowIso();
  const role = input.role ?? "coach";

  // Reuse existing link if there is one (live or tombstoned).
  const existing = (
    await selectAll<TeamCoachRow>(
      db,
      `SELECT * FROM team_coach WHERE team_id = ? AND user_id = ? LIMIT 1`,
      [input.teamId, input.userId],
    )
  )[0];
  const id = existing?.id ?? newId();
  const row: TeamCoachRow = {
    id,
    org_id: input.orgId,
    rev: existing?.rev ?? null,
    updated_at: updatedAt,
    deleted_at: null,
    client_id: null,
    team_id: input.teamId,
    user_id: input.userId,
    role,
  };

  await withLocalWrite(db, async (tx) => {
    if (existing) {
      await tx.exec(
        `UPDATE team_coach
           SET role = ?, deleted_at = NULL, updated_at = ?
           WHERE id = ?`,
        [role, updatedAt, id],
      );
    } else {
      await tx.exec(
        `INSERT INTO team_coach
           (id, org_id, rev, updated_at, deleted_at, client_id, team_id, user_id, role)
           VALUES (?, ?, NULL, ?, NULL, NULL, ?, ?, ?)`,
        [id, input.orgId, updatedAt, input.teamId, input.userId, role],
      );
    }
    await enqueueOp(tx, {
      entity: "team_coach",
      kind: "upsert",
      payload: {
        id,
        orgId: input.orgId,
        teamId: input.teamId,
        userId: input.userId,
        role,
        updatedAt,
        deletedAt: null,
      },
      updatedAt,
      baseRev: existing?.rev ?? null,
    });
  });

  return row;
}

/**
 * Prunes client-only "phantom" teams from the local mirror — rows that
 * were created locally (rev IS NULL) but no longer have a matching outbox
 * operation, meaning they were never pushed to the server and never will
 * be. Historically the app bootstrap auto-created a "Mein Team"
 * placeholder which is exactly this shape: it lived only in SQLite, never
 * roundtripped through sync, and re-appeared after every org switch.
 *
 * Real in-flight locally-created teams are protected by the outbox check
 * — as long as their `upsert` op is queued, this prune leaves them alone.
 */
export async function pruneOrphanedTeams(
  db: LocalDb,
  orgId: string,
): Promise<number> {
  const orphans = await selectAll<{ id: string }>(
    db,
    `SELECT t.id FROM team t
       WHERE t.org_id = ?
         AND t.rev IS NULL
         AND NOT EXISTS (
           SELECT 1 FROM sync_outbox o
             WHERE o.entity = 'team'
               AND json_extract(o.payload_json, '$.id') = t.id
         )`,
    [orgId],
  );
  if (orphans.length === 0) return 0;
  await withLocalWrite(db, async (tx) => {
    for (const o of orphans) {
      await tx.exec(`DELETE FROM team_coach WHERE team_id = ?`, [o.id]);
      await tx.exec(`DELETE FROM team WHERE id = ?`, [o.id]);
    }
  });
  return orphans.length;
}

/**
 * First-run helper. With Coach=User, we no longer seed a coach row —
 * the user IS the coach. We only need to ensure the org has at least
 * one team and a `team_coach` link so the rest of the UI has something
 * to anchor to.
 *
 * Idempotent: returns the existing first team (and creates the link
 * lazily) when one already exists.
 */
export async function ensureDefaultTeam(
  db: LocalDb,
  orgId: string,
  userId: string,
  fallbackTeamName = "Mein Team",
): Promise<{ team: TeamRow }> {
  const teams = await listTeams(db, orgId);
  let team = teams[0];
  if (!team) {
    team = await createTeam(db, { orgId, name: fallbackTeamName });
  }
  // Ensure the bootstrapping user is linked. Owners technically don't
  // need the link (server treats them as having every team), but we
  // write it so non-owner sessions converge to the same view.
  const links = await listTeamCoaches(db, orgId, team.id);
  if (!links.some((l) => l.user_id === userId)) {
    await assignCoachToTeam(db, {
      orgId,
      teamId: team.id,
      userId,
      role: "coach",
    });
  }
  return { team };
}

/** Helper for repos that want to spell out the owner ref explicitly. */
export interface OwnerRef {
  ownerKind: OwnerKind;
  ownerId: string;
}
